import { Box, Choicebox, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type {
    IExtraVisusExaminationFields,
    IVisusAutoRefractie,
    IVisusAutoRefractieCyclo,
    IVisusNabijeVisus,
    IVisusPinhole,
    IVisusRefractie,
    IVisusSkiascopieCyclo,
    IVisusSpleetlamp,
} from '../../../../../common/interfaces/ophthalmology/orthO/orthOProcedureCustomControlData';
import type { IOrthOV1ProcedureExaminationsProps } from './OrthOV1ProcedureExaminations';
import { OrthOExaminationOrder } from './orthOExaminationOrder';
import { OrthOV1ProcedureOdOsExaminationRow } from './OrthOV1OdOsExaminationRow';
import { OrthOV1ProcedureOdOsFindingsRow } from './OrthOV1ProcedureOdOsFindingsRow';
import { OrthOV1ProcedureOdOsOdsFindingsTable } from './OrthOV1ProcedureOdOsOdsFindingsTable';
import { ReadOrEditInputField } from './ReadOrEditInputField';

const extraVisusExaminations = [
    'Visus met pinhole',
    'Refractie',
    'Autorefractie',
    'Autorefractie in cyclo',
    'Skiascopie in cyclo',
    'Spleetlamponderzoek',
    'Dynamische skiascopie',
    'Visus nabij',
] as const;

type IOrthOExtraVisusExaminations = (typeof extraVisusExaminations)[number];

const visus_pinhole_fields: (keyof IVisusPinhole)[] = ['visus_pinhole_od', 'visus_pinhole_os'];

const visus_refractie_fields: (keyof IVisusRefractie)[] = [
    'refractie_sferisch_od',
    'refractie_cylindrisch_od',
    'refractie_as_od',
    'refractie_additie_od',
    'refractie_prisma_od',
    'refractie_sferisch_os',
    'refractie_cylindrisch_os',
    'refractie_as_os',
    'refractie_additie_os',
    'refractie_prisma_os',
];

const visus_autorefractie_fields: (keyof IVisusAutoRefractie)[] = [
    'autorefractie_sferisch_od',
    'autorefractie_cylindrisch_od',
    'autorefractie_as_od',
    'autorefractie_additie_od',
    'autorefractie_prisma_od',
    'autorefractie_sferisch_os',
    'autorefractie_cylindrisch_os',
    'autorefractie_as_os',
    'autorefractie_additie_os',
    'autorefractie_prisma_os',
];

const visus_autorefreactie_cyclo_fields: (keyof IVisusAutoRefractieCyclo)[] = [
    'autorefractie_cyclo_sferisch_od',
    'autorefractie_cyclo_cylindrisch_od',
    'autorefractie_cyclo_as_od',
    'autorefractie_cyclo_additie_od',
    'autorefractie_cyclo_prisma_od',
    'autorefractie_cyclo_prisma_degrees_od',
    'autorefractie_cyclo_sferisch_os',
    'autorefractie_cyclo_cylindrisch_os',
    'autorefractie_cyclo_as_os',
    'autorefractie_cyclo_additie_os',
    'autorefractie_cyclo_prisma_os',
    'autorefractie_cyclo_prisma_degrees_os',
];

const visus_skiascopie_fields: (keyof IVisusSkiascopieCyclo)[] = [
    'skiascopie_cyclo_sferisch_od',
    'skiascopie_cyclo_cylindrisch_od',
    'skiascopie_cyclo_as_od',
    'skiascopie_cyclo_sferisch_os',
    'skiascopie_cyclo_cylindrisch_os',
    'skiascopie_cyclo_as_os',
];

const visus_spleetlamp_fields: (keyof IVisusSpleetlamp)[] = ['spleetlamp_onderzoek_od', 'spleetlamp_onderzoek_os'];

const visus_nabij_fields: (keyof IVisusNabijeVisus)[] = [
    'nabije_visus_onderzoek_od',
    'nabije_visus_onderzoek_os',
    'nabije_visus_onderzoek_ods',
];

export function OrthOV1ProcedureSelectedExtraVisusExaminations(props: IOrthOV1ProcedureExaminationsProps) {
    const [selectedExtraVisusExaminations, setSelectedExtraVisusExaminations] = useState<
        IOrthOExtraVisusExaminations[]
    >([]);

    useEffect(() => {
        updateSelectedExtraVisusExaminations();
    }, []);

    return (
        <>
            {!props.isReadOnly && (
                <FormGroup label="Onderzoek toevoegen">
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={3}
                    >
                        {extraVisusExaminations.map((examination, index) => {
                            return (
                                <Choicebox
                                    checked={selectedExtraVisusExaminations.includes(examination)}
                                    key={`${examination}-${index}`}
                                    label={examination}
                                    name={examination}
                                    onChange={async () => await addOrRemoveExamination(examination)}
                                    tabIndex={OrthOExaminationOrder.notFocusable}
                                />
                            );
                        })}
                    </Box>
                </FormGroup>
            )}

            {selectedExtraVisusExaminations.includes('Visus met pinhole') && (
                <OrthOV1ProcedureOdOsFindingsRow
                    customControlData={props.customControlData}
                    errors={props.errors}
                    fieldKeyOd="visus_pinhole_od"
                    fieldKeyOs="visus_pinhole_os"
                    hasErrors={
                        props.errors?.has('customControlData.visus_pinhole_od') ||
                        props.errors?.has('customControlData.visus_pinhole_os')
                    }
                    isReadOnly={props.isReadOnly}
                    label="Visus met pinhole"
                />
            )}

            {selectedExtraVisusExaminations.includes('Refractie') && (
                <OrthOV1ProcedureOdOsExaminationRow
                    additie_od={props.customControlData.refractie_additie_od}
                    additie_os={props.customControlData.refractie_additie_os}
                    as_od={props.customControlData.refractie_as_od}
                    as_os={props.customControlData.refractie_as_os}
                    cylindrisch_od={props.customControlData.refractie_cylindrisch_od}
                    cylindrisch_os={props.customControlData.refractie_cylindrisch_os}
                    errors={props.errors}
                    field="refractie"
                    isReadOnly={props.isReadOnly}
                    label="Refractie"
                    prisma_od={props.customControlData.refractie_prisma_od}
                    prisma_os={props.customControlData.refractie_prisma_os}
                    sferisch_od={props.customControlData.refractie_sferisch_od}
                    sferisch_os={props.customControlData.refractie_sferisch_os}
                />
            )}

            {selectedExtraVisusExaminations.includes('Autorefractie') && (
                <OrthOV1ProcedureOdOsExaminationRow
                    additie_od={props.customControlData.autorefractie_additie_od}
                    additie_os={props.customControlData.autorefractie_additie_os}
                    as_od={props.customControlData.autorefractie_as_od}
                    as_os={props.customControlData.autorefractie_as_os}
                    cylindrisch_od={props.customControlData.autorefractie_cylindrisch_od}
                    cylindrisch_os={props.customControlData.autorefractie_cylindrisch_os}
                    errors={props.errors}
                    field="autorefractie"
                    isReadOnly={props.isReadOnly}
                    label="Autorefractie"
                    prisma_od={props.customControlData.autorefractie_prisma_od}
                    prisma_os={props.customControlData.autorefractie_prisma_os}
                    sferisch_od={props.customControlData.autorefractie_sferisch_od}
                    sferisch_os={props.customControlData.autorefractie_sferisch_os}
                />
            )}

            {selectedExtraVisusExaminations.includes('Autorefractie in cyclo') && (
                <OrthOV1ProcedureOdOsExaminationRow
                    additie_od={props.customControlData.autorefractie_cyclo_additie_od}
                    additie_os={props.customControlData.autorefractie_cyclo_additie_os}
                    as_od={props.customControlData.autorefractie_cyclo_as_od}
                    as_os={props.customControlData.autorefractie_cyclo_as_os}
                    cylindrisch_od={props.customControlData.autorefractie_cyclo_cylindrisch_od}
                    cylindrisch_os={props.customControlData.autorefractie_cyclo_cylindrisch_os}
                    errors={props.errors}
                    field="autorefractie_cyclo"
                    isReadOnly={props.isReadOnly}
                    label="Autorefractie in cyclo"
                    prisma_degrees_od={props.customControlData.autorefractie_cyclo_prisma_degrees_od}
                    prisma_degrees_os={props.customControlData.autorefractie_cyclo_prisma_degrees_os}
                    prisma_od={props.customControlData.autorefractie_cyclo_prisma_od}
                    prisma_os={props.customControlData.autorefractie_cyclo_prisma_os}
                    sferisch_od={props.customControlData.autorefractie_cyclo_sferisch_od}
                    sferisch_os={props.customControlData.autorefractie_cyclo_sferisch_os}
                />
            )}

            {selectedExtraVisusExaminations.includes('Skiascopie in cyclo') && (
                <OrthOV1ProcedureOdOsExaminationRow
                    as_od={props.customControlData.skiascopie_cyclo_as_od}
                    as_os={props.customControlData.skiascopie_cyclo_as_os}
                    cylindrisch_od={props.customControlData.skiascopie_cyclo_cylindrisch_od}
                    cylindrisch_os={props.customControlData.skiascopie_cyclo_cylindrisch_os}
                    errors={props.errors}
                    excludeAdditieAndPrisma
                    field="skiascopie_cyclo"
                    isReadOnly={props.isReadOnly}
                    label="Skiascopie in cyclo"
                    sferisch_od={props.customControlData.skiascopie_cyclo_sferisch_od}
                    sferisch_os={props.customControlData.skiascopie_cyclo_sferisch_os}
                />
            )}

            {selectedExtraVisusExaminations.includes('Spleetlamponderzoek') && (
                <OrthOV1ProcedureOdOsFindingsRow
                    customControlData={props.customControlData}
                    errors={props.errors}
                    fieldKeyOd="spleetlamp_onderzoek_od"
                    fieldKeyOs="spleetlamp_onderzoek_os"
                    hasErrors={
                        props.errors?.has('customControlData.spleetlamp_onderzoek_od') ||
                        props.errors?.has('customControlData.spleetlamp_onderzoek_os')
                    }
                    isReadOnly={props.isReadOnly}
                    label="Spleetlamp onderzoek (incl. kamerhoek beoordeling)"
                />
            )}

            {selectedExtraVisusExaminations.includes('Dynamische skiascopie') && (
                <ReadOrEditInputField
                    {...props}
                    error={props.errors?.get('customControlData.dynamische_skiascopie_onderzoek')}
                    fieldKey="dynamische_skiascopie_onderzoek"
                    isRequired={false}
                    label="orthOProcedure.dynamic_skiascopy"
                    placeholder="Bevindingen..."
                    tabIndex={OrthOExaminationOrder.extraVisusExaminationsDynamicSkiacopy}
                    value={props.customControlData.dynamische_skiascopie_onderzoek}
                />
            )}

            {selectedExtraVisusExaminations.includes('Visus nabij') && (
                <OrthOV1ProcedureOdOsOdsFindingsTable
                    errors={props.errors}
                    field="nabije_visus_onderzoek"
                    isReadOnly={props.isReadOnly}
                    label="Visus nabij"
                    tabIndexOd={OrthOExaminationOrder.extraVisusExaminationsVisionNearOd}
                    tabIndexOds={OrthOExaminationOrder.extraVisusExaminationsVisionNearOds}
                    tabIndexOs={OrthOExaminationOrder.extraVisusExaminationsVisionNearOs}
                    value_od={props.customControlData.nabije_visus_onderzoek_od}
                    value_ods={props.customControlData.nabije_visus_onderzoek_ods}
                    value_os={props.customControlData.nabije_visus_onderzoek_os}
                />
            )}
        </>
    );

    function updateSelectedExtraVisusExaminations() {
        const extraExaminationsToBeSelected = [...selectedExtraVisusExaminations];
        if (
            isAnyExaminationFieldFilled(visus_pinhole_fields) &&
            !selectedExtraVisusExaminations.includes('Visus met pinhole')
        ) {
            extraExaminationsToBeSelected.push('Visus met pinhole');
        }
        if (
            isAnyExaminationFieldFilled(visus_refractie_fields) &&
            !selectedExtraVisusExaminations.includes('Refractie')
        ) {
            extraExaminationsToBeSelected.push('Refractie');
        }
        if (
            isAnyExaminationFieldFilled(visus_autorefractie_fields) &&
            !selectedExtraVisusExaminations.includes('Autorefractie')
        ) {
            extraExaminationsToBeSelected.push('Autorefractie');
        }
        if (
            isAnyExaminationFieldFilled(visus_autorefreactie_cyclo_fields) &&
            !selectedExtraVisusExaminations.includes('Autorefractie in cyclo')
        ) {
            extraExaminationsToBeSelected.push('Autorefractie in cyclo');
        }
        if (
            isAnyExaminationFieldFilled(visus_skiascopie_fields) &&
            !selectedExtraVisusExaminations.includes('Skiascopie in cyclo')
        ) {
            extraExaminationsToBeSelected.push('Skiascopie in cyclo');
        }
        if (
            isAnyExaminationFieldFilled(visus_spleetlamp_fields) &&
            !selectedExtraVisusExaminations.includes('Spleetlamponderzoek')
        ) {
            extraExaminationsToBeSelected.push('Spleetlamponderzoek');
        }
        if (
            props.customControlData.dynamische_skiascopie_onderzoek &&
            !selectedExtraVisusExaminations.includes('Dynamische skiascopie')
        ) {
            extraExaminationsToBeSelected.push('Dynamische skiascopie');
        }
        if (
            isAnyExaminationFieldFilled(visus_nabij_fields) &&
            !selectedExtraVisusExaminations.includes('Visus nabij')
        ) {
            extraExaminationsToBeSelected.push('Visus nabij');
        }
        setSelectedExtraVisusExaminations(extraExaminationsToBeSelected);
    }

    function isAnyExaminationFieldFilled(fields: (keyof IExtraVisusExaminationFields)[]) {
        return fields
            .map((field) => {
                return props.customControlData[field];
            })
            .some((value) => !!value);
    }

    async function addOrRemoveExamination(examination: IOrthOExtraVisusExaminations) {
        if (!props.isReadOnly) {
            if (selectedExtraVisusExaminations.includes(examination)) {
                const examinationToFieldMapper = {
                    'Visus met pinhole': visus_pinhole_fields,
                    Refractie: visus_refractie_fields,
                    Autorefractie: visus_autorefractie_fields,
                    'Autorefractie in cyclo': visus_autorefreactie_cyclo_fields,
                    'Skiascopie in cyclo': visus_skiascopie_fields,
                    Spleetlamponderzoek: visus_spleetlamp_fields,
                    'Dynamische skiascopie': ['dynamische_skiascopie_onderzoek'],
                    'Visus nabij': visus_nabij_fields,
                };

                for (const field of examinationToFieldMapper[examination]) {
                    props.handleChangeCustomControl({ [field]: null });
                }
                await setSelectedExtraVisusExaminations([
                    ...selectedExtraVisusExaminations.filter((selectedExam) => selectedExam !== examination),
                ]);
            } else {
                await setSelectedExtraVisusExaminations([...selectedExtraVisusExaminations, examination]);
            }
        }
    }
}
